import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams, useBlocker } from 'react-router-dom';
import Modal from 'react-modal';
import { Helmet } from "react-helmet";
import BreadCrumbs from '../BreadCrumbs';
import Literature from './Literature';
import FinalReview from './FinalReview';
import RelevantCriteriaCategories from './RelevantCriteriaCategories';
import ConsensusReviews from '../ConsensusReviews';
import CalculatedClassification from './CalculatedClassification';
import Sources from './Sources';
import classificationLogic from './classificationLogic.js';
import collectEvaluations from './collectEvaluations.js';
import ACMGTable from './ACMGTable';
import SessionConstants from '../../config/SessionConstants.js';
import ClassificationWarning from './ClassificationWarning';
import ClassificationForm from './ClassificationForm/index.js';
import Loading from '../Loading';
import APIUtils from '../../utils/APIUtils';
import UnsavedChangesModal from '../../uiComponents/UnsavedChangesModal';
import AnnotationNamesConfig from './AnnotationNamesConfig';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StepperInterpretation from './StepperInterpretation/StepperInterpretation';
import WarningIcon from '@mui/icons-material/Warning';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container'
import './index.css';

const BENIGN = "Benign";
const LIKELY_BENIGN = "Likely Benign";
const UNCERTAIN_SIGNIFICANCE = "Uncertain Significance";
const LIKELY_PATHOGENIC = "Likely Pathogenic";
const PATHOGENIC = "Pathogenic";

export default function AddInterpretations() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Refs for stepper
  const nextRef = useRef();
  const backRef = useRef();

  // State
  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const [variantId, setVariantId] = useState(null);
  const [variantName, setVariantName] = useState('');
  const [programId, setProgramId] = useState(null);
  const [annoData, setAnnoData] = useState({ categories: [] });
  const [modalSubmitIsOpen, setModalSubmitIsOpen] = useState(false);
  const [modalCancelIsOpen, setModalCancelIsOpen] = useState(false);
  const [modalValidationIsOpen, setModalValidationIsOpen] = useState(false);
  const [validationRequiredFields, setValidationRequiredFields] = useState('');
  const [evaluationList, setEvaluationList] = useState([]);
  const [condition, setCondition] = useState(null);
  const [calcClassification, setCalcClassification] = useState('None');
  const [programClassification, setProgramClassification] = useState(null);
  const [comments, setComments] = useState('');
  const [summary, setSummary] = useState({});
  const [summaryExists, setSummaryExists] = useState(false);
  const [savedInterpretation, setSavedInterpretation] = useState(false);
  const [loadedAnnotations, setLoadedAnnotations] = useState(false);
  const [loadedInterpretation, setLoadedInterpretation] = useState(false);
  const [literatureEvaluationList, setLiteratureEvaluationList] = useState([]);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [categoryValues, setCategoryValues] = useState({
    DISEASE_DATABASES: {},
    POPULATION_FREQUENCY: {},
    COMPUTATIONAL_PREDICTIVE_PROGRAMS: {},
    NUCLEOTIDE_CONSERVATION: {},
  });
  const [formIsIncomplete, setFormIsIncomplete] = useState(true);
  // Mirror of the dirty flag for the navigation blocker. A ref lets the blocker
  // (registered once) read the latest value synchronously, so a navigate() fired
  // in the same tick we clear the flag isn't blocked by a stale reading.
  const formIsIncompleteRef = useRef(true);
  const markFormClean = () => {
    formIsIncompleteRef.current = false;
    setFormIsIncomplete(false);
  };
  const [errorMsg, setErrorMsg] = useState('');
  const [fieldDescription, setFieldDescription] = useState({});
  const [toVariantDetails, setToVariantDetails] = useState(false);
  const [loading, setLoading] = useState(false);

  // Extract state from location (react-router v6)
  const state = location.state || {};
  const reviewStatus = state.reviewStatus;
  const variantInterpretationId = state.variantInterpretationId;
  const gene = state.gene;
  // Set when the user opened this form from the Program Summary "Assigned to Me" button;
  // exits (cancel/save/submit) should return there instead of the variant details page.
  const fromProgramSummary = state.fromProgramSummary;

  // Navigation guard (replaces <Prompt>). The blocker function is registered
  // once and reads the dirty flag from a ref, so it always sees the latest value
  // synchronously. Intentional exits (save/submit/discard) call markFormClean()
  // before navigate(), so the router never blocks the app's own navigations and
  // can't get stuck. Only genuine user-initiated exits (links, browser back)
  // reach the confirmation modal below.
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        formIsIncompleteRef.current &&
        currentLocation.pathname !== nextLocation.pathname,
      []
    )
  );

  // Handler for accepting navigation
  const handleAcceptNavigation = () => {
    blocker.proceed?.();
  };

  // Handler for cancelling navigation
  const handleCancelNavigation = () => {
    blocker.reset?.();
  };

  // Load initial data
  useEffect(() => {
    let crumbs = state.breadCrumbs ? [...state.breadCrumbs, { name: 'AddInterpretation' }] : [{ name: 'AddInterpretation' }];
    setBreadCrumbs(crumbs);
    setCondition(state.potentialCondition);
    setProgramId(sessionStorage.getItem(SessionConstants.PROGRAM_ID));
    setVariantId(state.variantId);
    setVariantName(state.variantName);
    setFormIsIncomplete(true);
    loadAnnotations(state.variantId);
    loadInterpretation(state.variantId);
    loadFieldDescription();
  }, []);

  async function loadAnnotations(variantId) {
    setLoading(true);
    var annoDataResp = await APIUtils.getAnnotationsForVariant(variantId);

    if (typeof annoDataResp !== "string") {
      const summaryObj = {
        gene: annoDataResp.geneId,
        condition: annoDataResp.condition,
        variantId: variantId
      };
      setSummary(summaryObj);
      setSummaryExists(true);
      setAnnoData(annoDataResp);
      setLoadedAnnotations(true);
      setLoading(false);
    } else {
      setErrorMsg(annoDataResp);
      setLoadedAnnotations(false);
      setLoading(false);
      console.error('There was an error loading the annotations.');
    }
  }

  async function loadFieldDescription() {
    var response = await APIUtils.getFieldDescription();
    if (response) setFieldDescription(response);
  }

  async function loadInterpretation(variantId) {
    var response = await APIUtils.getVariantInterpretation(sessionStorage.getItem(SessionConstants.USER_EMAIL), variantId);
    if (response) {
      const categoryValuesBuilder = { ...categoryValues };

      if (Array.isArray(response.interpretationDetails) && response.interpretationDetails.length > 0) {
        response.interpretationDetails.forEach(currentEval => {
          categoryValuesBuilder[currentEval.AnnotationCategory].comments = currentEval.Comment;
          if ((currentEval.AcmgCriteria !== undefined) && (currentEval.AcmgCriteria !== null)) {
            categoryValuesBuilder[currentEval.AnnotationCategory].eval = currentEval.AcmgCriteria;
          }
        });
      }
      setProgramClassification(response.programClassification !== 'null' ? response.programClassification : '');
      setCalcClassification(response.calcClassification !== 'null' ? response.calcClassification : '');
      setComments(response.comment);
      setCategoryValues(categoryValuesBuilder);
      setLoadedInterpretation(true);
      setEvaluationList(collectEvaluations(categoryValuesBuilder));
    } else {
      setErrorMsg('There was an error retrieving the interpretation.');
      setLoadedInterpretation(false);
    }
  }

  // Modal handlers
  const openCancelModal = () => setModalCancelIsOpen(true);

  const openSubmitModal = () => {
    if (programClassification && comments) {
      setModalSubmitIsOpen(true);
    } else {
      let validationRequiredFieldsArr = [];
      if (!programClassification) validationRequiredFieldsArr.push("Classification");
      if (!comments) validationRequiredFieldsArr.push("Comments");
      setValidationRequiredFields(validationRequiredFieldsArr.join(", "));
      setModalValidationIsOpen(true);
    }
  };

  const closeModal = () => {
    setModalCancelIsOpen(false);
    setModalSubmitIsOpen(false);
    setModalValidationIsOpen(false);
  };

  // Navigation after discard
  const onDiscard = () => {
    markFormClean();
    window.onbeforeunload = null;
    closeModal();
    if (fromProgramSummary) {
      navigate('/programsummary', { replace: true });
      return;
    }
    navigate('/variantdetails/' + variantId, {
      state: {
        programId: programId,
        variantId: variantId,
        variantName: variantName,
        justLoadedInterpretation: true,
        breadCrumbs: state.breadCrumbs,
      },
      replace: true // After saving, replace the history so back button doesn't return to the form
    });
  };

  // Save and submit. Keep the dirty guard active until persistence succeeds so a
  // failed save doesn't silently allow the still-unsaved form to be navigated away.
  const save = () => {
    saveInterpretation(true);
  };

  const submit = () => {
    saveInterpretation(false);
  };

  async function saveInterpretation(partialStatus) {
    const categoryValuesObject = { ...categoryValues };
    let interpretationDetails = [];
    const keysToRemove = ["sourceDescription", "annotationContent"];

    for (const key of Object.keys(categoryValuesObject)) {
      let evalList = {};
      evalList['AnnotationCategory'] = key;
      evalList['AcmgCriteria'] = categoryValuesObject[key].eval;
      evalList['Comment'] = categoryValuesObject[key].comments;
      if (!partialStatus){
              // If not partial status, include version info
              evalList['versionInfo'] = annoData.categories.find(category => category.categoryName === key)?.annotationSources
                                                           ?.map(obj => Object.fromEntries(
                                                                         Object.entries(obj).filter(([key]) => !keysToRemove.includes(key))
                                                              )) ?? [];
      }
    
      interpretationDetails.push(evalList);
    }
   
    let formData = new FormData();
    formData.append("userId", sessionStorage.getItem(SessionConstants.USER_EMAIL));
    formData.append("interpretationStatus", partialStatus ? 'IN_PROGRESS' : 'COMPLETED');
    formData.append("IsReview", false);
    formData.append("calcClassification", calcClassification || '');
    formData.append("programClassification", programClassification || '');
    formData.append("comment", comments || ''); // default to empty string if null - this would save to database as null (not string "null")
    formData.append("interpretationDetails", JSON.stringify(interpretationDetails));
    
    var response = await APIUtils.updateVariantInterpretation(programId, variantId, formData);

    if (response) {
      markFormClean();
      setSavedInterpretation(true);
    } else {
      setErrorMsg('There was an error uploading the interpretation.');
    }
  }

  // Navigation after save
  useEffect(() => {
    if (savedInterpretation) {
      window.onbeforeunload = () => null;
      setToVariantDetails(true);
    }
  }, [savedInterpretation]);

  useEffect(() => {
    if (toVariantDetails) {
      if (fromProgramSummary) {
        navigate('/programsummary', { replace: true });
        return;
      }
      navigate('/variantdetails/' + variantId, {
        state: {
          variantId: variantId,
          variantName: variantName,
          breadCrumbs: state.breadCrumbs,
          programId: programId,
          justAddedInterpretation: true,
          reviewStatus: reviewStatus,
        },
        replace: true // After saving, replace the history so back button doesn't return to the form
      });
    }
  }, [toVariantDetails, navigate, variantId, variantName, state.breadCrumbs, programId, reviewStatus, fromProgramSummary]);

  // Evaluation and classification logic
  const onEvaluationChange = (source, evaluation) => {
    const categoryValueObj = {
      ...categoryValues,
      [source]: { ...categoryValues[source], eval: evaluation },
    };
    setCategoryValues(categoryValueObj);
    updateClassification(collectEvaluations(categoryValueObj), literatureEvaluationList);
  };

  const onLiteratureEvaluationChange = (literatureEvaluations) => {
    setLiteratureEvaluationList(literatureEvaluations);
    updateClassification(evaluationList, literatureEvaluations);
  };

  const onClassificationChange = (event) => {
    setProgramClassification(event.target.value);
  };

  const onTextChange = (source, newText) => {
    const categoryValuesObj = { ...categoryValues };
    if (!categoryValuesObj[source].comments) {
      categoryValuesObj[source].comments = '';
    }
    categoryValuesObj[source].comments = newText;
    setCategoryValues(categoryValuesObj);
  };

  function updateClassification(evaluationListArg, literatureEvaList) {
    let classification = classificationLogic(evaluationListArg, literatureEvaList);
    setEvaluationList(evaluationListArg);
    setCalcClassification(classification);
    setProgramClassification(classification);
  }

  function loadAnnotationSection(source) {
    let sourceString = AnnotationNamesConfig[source.categoryName];
    return (
      <div className="section" key={source.categoryName}>
        <h2 className="sectionHeading" tabIndex={-1}>{sourceString}</h2>
        <div className="sectionContent">
          <RelevantCriteriaCategories sourceType={sourceString} />
          <Sources
            fieldDescription={fieldDescription}
            sourceType={sourceString}
            categoryType={source.categoryName}
            categoryValues={categoryValues}
            annoData={source}
            onEvaluationChange={onEvaluationChange}
            onTextChange={onTextChange}
          />
        </div>
      </div>
    );
  }

  // Stepper navigation
  const nextIndex = () => {
    if (activeStepIndex < 7) setActiveStepIndex(activeStepIndex + 1);
  };

  const backIndex = () => {
    if (activeStepIndex !== 0) setActiveStepIndex(activeStepIndex - 1);
  };

  // Build annotation sections
  let annoSections = [];
  if (annoData.categories) {
    annoSections = annoData.categories.map(category => loadAnnotationSection(category));
  }

  return (
    <>
      <Loading isLoading={loading} />
      <Helmet>
        <title>ED3N - Add Interpretation</title>
      </Helmet>
      <BreadCrumbs breadCrumbs={breadCrumbs} />
      <h1 className="pageTitle">
        Add Interpretation: {variantName}
      </h1>
      <StepperInterpretation
        activeStepIndex={activeStepIndex}
        ref={{
          ref1: backRef,
          ref2: nextRef
        }}
      />
      <div className="interpretationLayout">
      <div className="interpretation-container">
        {!loadedAnnotations && errorMsg ? (
          <>
            <div className='errorMsg'>
              <ErrorOutlineIcon fontSize='small' /> {errorMsg}
            </div>
            <div style={{ minHeight: '600px' }}></div>
          </>
        ) : !loadedAnnotations ? (
          <div style={{ minHeight: '600px' }}></div>
        ) : (
          activeStepIndex === 0 ? <>{annoSections[0]}</>
            : activeStepIndex === 1 ? <>{annoSections[1]}</>
              : activeStepIndex === 2 ? <>{annoSections[2]}</>
                : activeStepIndex === 3 ? <>{annoSections[3]}</>
                  : activeStepIndex === 4 ? (
                    <>
                      <div className="section">
                        <h2 className="sectionHeading" tabIndex={-1}>Literature</h2>
                        <div className="sectionContent">
                          <Literature
                            sourceType="Literature"
                            categoryValues={categoryValues}
                            gene={gene}
                            variantName={variantName}
                            variantId={variantId}
                            variantInterpretationId={variantInterpretationId}
                            onLiteratureEvaluationChange={onLiteratureEvaluationChange}
                          />
                        </div>
                      </div>
                    </>
                  ) : activeStepIndex === 5 ? (
                    <>
                      <div className="section">
                        <h2 className="sectionHeading" tabIndex={-1}>Interpretation</h2>
                        <Container maxWidth="xl">
                          <Grid container spacing={3} className="dashGridContainer">
                            <Grid item xs={12} sm={8} md={8}>
                              <Paper className="card" elevation={0}>
                                <div className='interpretationItem'>
                                  <h3>Selected ACMG Criteria</h3>
                                  <ACMGTable evaluationList={evaluationList} literatureEvaluationList={literatureEvaluationList} />
                                </div>
                                <div className='interpretationItem'>
                                  <h3>Summary of Previous Consensus Reviews</h3>
                                  <ConsensusReviews variantId={variantId} />
                                </div>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} sm={4} md={4}>
                              <Paper className="card" elevation={0}>
                                <ClassificationForm
                                  summaryExists={summaryExists}
                                  summary={summary}
                                  programClassification={programClassification}
                                  comments={comments}
                                  handleOptionChange={onClassificationChange}
                                  setComments={setComments}
                                  BENIGN={BENIGN}
                                  LIKELY_BENIGN={LIKELY_BENIGN}
                                  UNCERTAIN_SIGNIFICANCE={UNCERTAIN_SIGNIFICANCE}
                                  LIKELY_PATHOGENIC={LIKELY_PATHOGENIC}
                                  PATHOGENIC={PATHOGENIC}
                                />
                              </Paper>
                            </Grid>
                          </Grid>
                        </Container>
                      </div>
                    </>
                  ) : activeStepIndex === 6 ? (
                    <>
                      <div className="section">
                        <h2 className="sectionHeading" tabIndex={-1}>Final Review</h2>
                        <div className="sectionContent">
                          <FinalReview
                            categoryValues={categoryValues}
                            programClassification={programClassification}
                            comments={comments}
                            variantInterpretationId={variantInterpretationId}
                          />
                        </div>
                      </div>
                    </>
                  ) : <></>
        )}
      </div>
      <div className="interpretationButtons">
        <button className="subtleButton"
          onClick={openCancelModal}
        >
          Cancel
        </button>
        <button className="outlinedButton"
          disabled={!loadedAnnotations || activeStepIndex === 6}
          onClick={save}
        >
          Save for Later
        </button>
        <CalculatedClassification calculatedClassification={calcClassification} />
        <button className="outlinedButton"
          disabled={activeStepIndex === 0}
          onClick={() => {
            backRef.current?.handleBack();
            backIndex();
          }}
          onKeyDown={event => {
            const targetElement = document.querySelector('.sectionHeading');
            if (event.key === 'Enter') {
              event.preventDefault();
              backRef.current?.handleBack();
              backIndex();
              if (targetElement) targetElement.focus();
            }
          }}
        >
          Prev
        </button>
        {activeStepIndex !== 6 ?
          <button className="primaryButton"
            disabled={!loadedAnnotations || (activeStepIndex === 6 && (!programClassification || comments === ""))}
            onClick={() => {
              nextRef.current?.handleNext();
              nextIndex();
            }}
            onKeyDown={event => {
              const targetElement = document.querySelector('.sectionHeading');
              if (event.key === 'Enter') {
                event.preventDefault();
                nextRef.current?.handleNext();
                nextIndex();
                if (targetElement) targetElement.focus();
              }
            }}
          >
            Next
          </button>
          : <></>
        }
        {activeStepIndex === 6 ?
          <button className="primaryButton"
            onClick={openSubmitModal}
          >
            Submit
          </button>
          : <></>
        }
      </div>
      </div>
      {/* Modals */}
      <Modal
        className="modal"
        overlayClassName="modalOverlay"
        aria-modal="true"
        contentLabel="Form Incomplete"
        shouldCloseOnOverlayClick={false}
        isOpen={modalValidationIsOpen}
        onRequestClose={closeModal}
      >
        <div className="modalContent flexContent">
          <span className="requiredIndicator">
            <WarningIcon fontSize='large' className='validationWarning' />
          </span>
          <div className="textContent">
            Required fields missing: <strong>{validationRequiredFields}</strong>.
            Go back to Step 6, "Interpretation", and complete all required fields.
          </div>
        </div>
        <div className="modalButtons">
          <button
            className="modalButton primaryButton"
            onClick={closeModal}
          >
            OK
          </button>
        </div>
      </Modal>
      <Modal
        className="modal"
        overlayClassName="modalOverlay"
        aria-modal="true"
        contentLabel="Confirm Submit"
        shouldCloseOnOverlayClick={false}
        isOpen={modalSubmitIsOpen}
        onRequestClose={closeModal}
      >
        <div className="modalContent center">
          <ClassificationWarning
            calcClassification={calcClassification}
            classification={programClassification} />
          Are you sure you want to submit this interpretation?
          <br />
          <br />
          <strong>Note:</strong> Hitting submit will notify your team that this variant interpretation cannot be changed and no other edits can be made.
        </div>
        <div className="modalButtons">
          <button className="modalButton primaryButton" onClick={submit}>
            Yes, Submit
          </button>
          <button
            className="modalButton"
            onClick={closeModal}
          >
            Cancel
          </button>
        </div>
      </Modal>
      <UnsavedChangesModal
        isOpen={modalCancelIsOpen}
        onConfirm={onDiscard}
        onCancel={closeModal}
      />
      <UnsavedChangesModal
        isOpen={blocker && blocker.state === "blocked"}
        onConfirm={handleAcceptNavigation}
        onCancel={handleCancelNavigation}
      />
    </>
  );
}